import { Brain, Lightbulb, Zap } from 'lucide-react';
import type { ReactNode } from 'react';
import { createElement } from 'react';

import type { PackMode } from '@/types/pricing';
import type { ThinkingMode } from '@/types/thinkingTypes';

/** Canonical order of pack modes for UI rendering (tabs, grids, etc.). */
export const PACK_MODES: readonly PackMode[] = ['standard', 'full', 'deep'];

/**
 * Translation key suffix for each mode's display label.
 * Used with `useTranslations('PricingPage')` — keys exist in sk.json/en.json.
 */
export const MODE_TRANSLATION_KEYS: Record<PackMode, string> = {
  standard: 'modeStandard',
  full: 'modeFull',
  deep: 'modeDeep',
};

/**
 * Icon for each pack mode. Returns a React element, not a component,
 * so consumers don't need to import the icon library themselves.
 */
export const MODE_ICONS: Record<PackMode, ReactNode> = {
  standard: createElement(Zap, { className: 'h-4 w-4', 'aria-hidden': 'true' }),
  full: createElement(Lightbulb, {
    className: 'h-4 w-4',
    'aria-hidden': 'true',
  }),
  deep: createElement(Brain, { className: 'h-4 w-4', 'aria-hidden': 'true' }),
};

/**
 * Semantic color class for each mode (used by pack card icons, tab highlights).
 * Uses the existing Lexomat design tokens.
 */
export const MODE_COLOR_CLASS: Record<PackMode, string> = {
  standard: 'text-content-primary',
  full: 'text-brand-tertiary',
  deep: 'text-status-info',
};

/**
 * Maps the UI's thinking mode ('fast'|'thinking'|'deep') to the pack mode
 * identifier ('standard'|'full'|'deep'). The two systems have different
 * historical names for the same three tiers.
 */
export const THINKING_TO_PACK_MODE: Record<ThinkingMode, PackMode> = {
  fast: 'standard',
  thinking: 'full',
  deep: 'deep',
};
